<template>
  <div class="chore-assign-modal">
    <CustomModal
      @close="$emit('close')"
      @ok="saveChanges"
      :title="'Edit Chore'"
      :okButtonText="'Save Changes'"
    >
      <div slot="body">
        <div class="error-form" v-if="!isValid">
          * Please fill required fields
        </div>
        <div class="create-assignment-error" v-if="showErrorMessage">
          <img src="../../../assets/error_red.png" />
          <span>{{ errorMessage }}</span>
        </div>
        <div class="form-wrapper" :class="{ error: !propertyId && !isValid }">
          <label>Property</label>
          <select
            v-model="propertyId"
            @change="getChildData"
            placeholder="Select Property"
          >
            <option :value="null">Select Property</option>
            <option
              v-for="property in properties"
              :key="'property-' + property.uuid"
              :value="property.uuid"
            >
              {{ property.internalName }}
            </option>
          </select>
        </div>
        <div class="form-wrapper" :class="{ error: !choreId && !isValid }">
          <label>Chore</label>
          <select v-model="choreId" placeholder="Select Chore">
            <option :value="null">Select Chore</option>
            <option
              v-for="(chore, index) of chores"
              :key="'chore-' + index + chore.uuid"
              :value="chore.uuid"
            >
              {{ chore.name }}
            </option>
          </select>
        </div>
        <div class="double-form">
          <div
            class="chore-assignment-selector-subcontainer"
            :class="{ error: !this.current.selectedDate && !isValid }"
          >
            <label
              >Date
              <label class="error-label" v-if="!validTime"
                >* {{ timeErrorMessage }}
              </label>
            </label>
            <DatePicker
              v-model="current.selectedDate"
              value-type="format"
              format="YYYY-MM-DD"
              placeholder="Select Date"
              @change="getChildData"
            />
          </div>
          <div
            class="form-wrapper"
            :class="{ error: !housemateId && !isValid }"
          >
            <label>Housemate</label>
            <select v-model="housemateId" placeholder="Select Housemate">
              <option :value="null">Select Housemate</option>
              <option
                v-for="person of housemates"
                :key="'housemate-' + person.housemate.uuid"
                :value="person.housemate.uuid"
              >
                {{ person.housemate.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </CustomModal>
  </div>
</template>

<script>
import CustomModal from '@/components/shared/modal/CustomModal.vue'
import DatePicker from 'vue2-datepicker'
import calls from '@/api/calls'
import moment from 'moment'

export default {
  props: {
    current: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    CustomModal,
    DatePicker
  },
  data() {
    return {
      propertyId: null,
      choreId: null,
      housemateId: null,
      properties: [],
      chores: [],
      housemates: [],
      isValid: true,
      housemateObject: null,
      validTime: true,
      timeErrorMessage: '',
      errorMessage: '',
      showErrorMessage: false
    }
  },
  methods: {
    async getProperties() {
      const { data, message } = await calls.GET('properties')
      this.properties = data
    },
    async getChores() {
      const { data, message } = await calls.GET(
        `chores/from/a/${this.propertyId}`
      )
      this.chores = data
    },
    async getHousemates() {
      if (this.propertyId == null) {
        const { message, data } = await calls.GET('housemates')
        this.housemates = data
      } else {
        if (this.current.selectedDate) {
          const { message, data } = await calls.GET(
            `housemates/from/a/${this.propertyId}/${this.current.selectedDate}`
          )
          this.housemates = data
        }
      }
    },
    getChildData() {
      this.isValid = true
      if (this.propertyId !== this.current.propertyId) {
        this.housemateId = null
        this.choreId = null
        this.housemates = []
        this.chores = []
      } else {
        this.housemateId = this.current.housemateId
        this.choreId = this.current.choreId
      }
      this.getChores()
      this.getHousemates()
    },
    validate() {
      this.isValid = true // Flush this flag

      if (
        !this.propertyId ||
        !this.choreId ||
        !this.housemateId ||
        !this.current.selectedDate
      ) {
        this.isValid = false
      } else {
        // Get housemateObject, in case user edits the housemate
        this.housemateObject = this.housemates.find(
          (person) => person.housemate.uuid == this.housemateId
        )
        if (this.housemateObject) {
          for (let i in this.housemateObject.rentals) {
            if (
              moment(this.current.selectedDate).isBetween(
                moment(this.housemateObject.rentals[i].arrival).format(
                  'YYYY-MM-DD'
                ),
                moment(this.housemateObject.rentals[i].departure).format(
                  'YYYY-MM-DD'
                )
              )
            ) {
              this.isValid = true
              this.validTime = true
              this.timeErrorMessage = null
              this.showErrorMessage = false
              break
            } else {
              this.isValid = false
              this.validTime = false
              this.timeErrorMessage = 'Select a different date'
              this.showErrorMessage = true
              this.errorMessage =
                "CAN'T assign a chore to someone who will not be staying at the property on the selected date"
            }
          }
        } else {
          this.housemateId = null
          this.isValid = false
        }
      }
    },
    async saveChanges() {
      let property_selected = this.properties.filter(
        (p) => p.uuid == this.propertyId
      )[0]
      this.validate()
      if (this.isValid) {
        const originalDate = this.current.originalDate
          ? this.current.originalDate
          : this.current.date
        await calls.DELETE(`assignment/${this.current.assignmentId}`)
        const { message, data } = await calls.POST(
          `assignment/${this.housemateId}/${this.choreId}`,
          {
            date: moment(new Date(this.current.selectedDate))
              .utcOffset(property_selected.utcoffset)
              .toISOString(),
            originalDate
          }
        )
        this.$emit('saved')
      }
    }
  },
  async mounted() {
    this.$emit('loading', true)
    try {
      this.propertyId = this.current.propertyId
      this.housemateId = this.current.housemateId
      this.choreId = this.current.choreId
      await this.getProperties()
      await this.getChores()
      await this.getHousemates()
    } finally {
      this.$emit('loading', false)
    }
  }
}
</script>

<style>
.chore-assign-modal label {
  display: block;
  margin-bottom: 5px;
  font-size: 13px;
}
.chore-assign-modal .form-wrapper {
  text-align: left;
  font-size: 13px;
  padding-bottom: 10px;
  color: #4a4a4a;
}

.chore-assign-modal .modal-mask {
  z-index: 1;
}

.chore-assign-modal .form-wrapper div.mx-datepicker {
  width: 90%;
}

.chore-assign-modal .form-wrapper.error div.mx-datepicker {
  border: 1px solid red !important;
}

.chore-assign-modal .double-form {
  display: grid;
  grid-template-columns: 50% 50%;
  gap: 16px;
}

.chore-assign-modal .form-wrapper.error select {
  border: 1px solid red !important;
}

.chore-assign-modal .form-wrapper select {
  width: 100%;
  border: 1px solid #cbd4dc;
  border-radius: 5px;
  height: 40px;
  cursor: pointer;
}

.chore-assign-modal .form-wrapper .mx-input {
  cursor: pointer;
}

.chore-assign-modal .mx-input {
  height: 26px;
}

.chore-assign-modal .error-form {
  color: red;
  padding-bottom: 20px;
  color: red;
  text-align: left;
  font-size: 16px;
}

.chore-assignment-selector-subcontainer {
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  text-align: left;
}

.error-label {
  color: red;
}

.create-assignment-error {
  padding: 12px;
  width: 100%;
  color: red;
  background: rgba(223, 0, 15, 0.05);
  border-radius: 6px;
}

.create-assignment-error span {
  margin-left: 12px;
  color: #df000f;
  font-family: Montserrat;
  font-weight: 900;
  font-size: 13px;
  line-height: 18px;
}
</style>
